import { RobotCloudAPI, } from '@moderepo/mode-apis';
import { searchParamsToString, } from '@moderepo/mode-data-state-base';
/**
 * The types of action that can be dispatched to update this sub state.
 */
export var MonitorsAndIssuesActionType;
(function (MonitorsAndIssuesActionType) {
    MonitorsAndIssuesActionType["SET_MONITORS"] = "set project monitors";
    MonitorsAndIssuesActionType["SET_MONITOR"] = "set project monitor";
    MonitorsAndIssuesActionType["CLEAR_MONITORS"] = "reset project monitors";
    MonitorsAndIssuesActionType["SET_ISSUES"] = "set project issues";
    MonitorsAndIssuesActionType["SET_ISSUE"] = "set project issue";
    MonitorsAndIssuesActionType["CLEAR_ISSUES"] = "clear project issues";
    MonitorsAndIssuesActionType["SET_ISSUE_COMMENTS"] = "set issue comments";
    MonitorsAndIssuesActionType["SET_ISSUE_COMMENT"] = "set issue comment";
    MonitorsAndIssuesActionType["CLEAR_ISSUE_COMMENTS"] = "clear issue comment";
})(MonitorsAndIssuesActionType || (MonitorsAndIssuesActionType = {}));
export const setMonitors = (projectId, monitors, searchParams) => {
    return {
        type: MonitorsAndIssuesActionType.SET_MONITORS,
        projectId,
        monitors,
        searchParams,
    };
};
export const setMonitor = (projectId, monitor) => {
    return {
        type: MonitorsAndIssuesActionType.SET_MONITOR,
        projectId,
        monitor,
    };
};
export const clearMonitors = (projectId) => {
    return {
        type: MonitorsAndIssuesActionType.CLEAR_MONITORS,
        projectId,
    };
};
export const setIssues = (projectId, issues, searchParams) => {
    return {
        type: MonitorsAndIssuesActionType.SET_ISSUES,
        projectId,
        issues,
        searchParams,
    };
};
export const setIssue = (projectId, issue) => {
    return {
        type: MonitorsAndIssuesActionType.SET_ISSUE,
        projectId,
        issue,
    };
};
export const clearIssues = (projectId) => {
    return {
        type: MonitorsAndIssuesActionType.CLEAR_ISSUES,
        projectId,
    };
};
export const setIssueComments = (projectId, issueId, comments, searchParams) => {
    return {
        type: MonitorsAndIssuesActionType.SET_ISSUE_COMMENTS,
        projectId,
        issueId,
        comments,
        searchParams,
    };
};
export const setIssueComment = (projectId, issueId, comment) => {
    return {
        type: MonitorsAndIssuesActionType.SET_ISSUE_COMMENT,
        projectId,
        issueId,
        comment,
    };
};
export const clearIssueComments = (projectId, issueId) => {
    return {
        type: MonitorsAndIssuesActionType.CLEAR_ISSUE_COMMENTS,
        projectId,
        issueId,
    };
};
/**
 * Get data for ALL Monitors in a project
 * @param projectId - The id of the project
 */
export const fetchMonitors = (projectId, pageNumber, pageSize, filters) => {
    return async (dataDispatch) => {
        const monitors = await RobotCloudAPI.getInstance().getMonitors(projectId, pageSize * pageNumber, pageSize, filters);
        dataDispatch(setMonitors(projectId, monitors, searchParamsToString({
            pageNumber, pageSize, ...filters,
        })));
    };
};
/**
 * Get 1 monitor from a project
 * @param projectId - The id of the project
 */
export const fetchMonitor = (projectId, monitorId) => {
    return async (dataDispatch) => {
        const monitor = await RobotCloudAPI.getInstance().getMonitor(projectId, monitorId);
        dataDispatch(setMonitor(projectId, monitor));
    };
};
/**
 * Create a new monitor
 * @param projectId - The id of the project
 */
export const createMonitor = (projectId, agentId, data) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().createMonitor(projectId, agentId, data);
        // clear the current list of Monitors because it is no longer valid
        dataDispatch(clearMonitors(projectId));
    };
};
/**
 * Update a Monitor
 * @param projectId - The id of the project
 */
export const updateMonitor = (projectId, agentId, monitorId, data) => {
    return async () => {
        await RobotCloudAPI.getInstance().updateMonitor(projectId, agentId, monitorId, data);
    };
};
/**
 * Delete a Monitor
 * @param projectId
 * @param agentId
 * @param monitorId
 */
export const deleteMonitor = (projectId, monitorId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().deleteMonitor(projectId, monitorId);
        // clear the current list of monitors because it is no longer valid
        await dataDispatch(clearMonitors(projectId));
    };
};
/**
 * Get data for ALL issues in a project
 * @param projectId - The id of the project
 * @param limit
 * @param skip
 * @param filters - OPTIONAL - additional filters
 */
export const fetchIssues = (projectId, filters) => {
    return async (dataDispatch) => {
        var _a;
        const modifiedFilters = {
            ...filters,
            skip: filters.pageNumber * filters.pageSize,
            limit: (_a = filters.pageSize) !== null && _a !== void 0 ? _a : 0,
            pageNumber: undefined,
            pageSize: undefined,
        };
        const issues = await RobotCloudAPI.getInstance().getIssues(projectId, modifiedFilters);
        await dataDispatch(setIssues(projectId, issues, searchParamsToString(filters)));
    };
};
/**
 * Get 1 issue from a project
 * @param projectId - The id of the project
 */
export const fetchIssue = (projectId, issueId) => {
    return async (dataDispatch) => {
        const issue = await RobotCloudAPI.getInstance().getIssue(projectId, issueId);
        await dataDispatch(setIssue(projectId, issue));
    };
};
/**
 * Manually create an Issue
 * @param projectId - The id of the project
 */
export const createIssue = (projectId, agentId, data) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().createIssue(projectId, agentId, data);
        // clear the current list of issues because it is no longer valid
        await dataDispatch(clearIssues(projectId));
    };
};
/**
 * Update an issue
 * @param projectId - The id of the project
 */
export const updateIssue = (projectId, agentId, issueId, data) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateIssue(projectId, agentId, issueId, data);
        // TODO - decide whether to reload the issue or just update the local cache of the issue with the provided params
        await dataDispatch(fetchIssue(projectId, issueId));
    };
};
/**
 * Delete an issue
 * @param projectId
 * @param agentId
 * @param issueId
 */
export const deleteIssue = (projectId, issueId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().deleteIssue(projectId, issueId);
        // clear the current list of issues because it is no longer valid
        await dataDispatch(clearIssues(projectId));
    };
};
/**
 * Fetch all comments for a specific issue
 * @param projectId
 * @param issueId
 * @param filters
 */
export const fetchAllIssueComments = (projectId, issueId, filters) => {
    return async (dataDispatch) => {
        const comments = await RobotCloudAPI.getInstance().getAllIssueComments(projectId, issueId);
        await dataDispatch(setIssueComments(projectId, issueId, comments, searchParamsToString(filters)));
    };
};
/**
 * Fetch a single comment for a specific issue
 * @param projectId
 * @param issueId
 * @param commentId
 */
export const fetchIssueComment = (projectId, issueId, commentId) => {
    return async (dataDispatch) => {
        const comment = await RobotCloudAPI.getInstance().getIssueComment(projectId, issueId, commentId);
        await dataDispatch(setIssueComment(projectId, issueId, comment)); // Add the fetched comment to the data state
    };
};
/**
 * Add comment for an issue
 * @param projectId
 * @param issueId
 * @param agentId
 * @param message
 */
export const addCommentForIssue = (projectId, issueId, agentId, message) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().addCommentForIssue(projectId, issueId, agentId, message);
        await dataDispatch(clearIssueComments(projectId, issueId)); // clear the current list of comments because it is no longer valid
    };
};
/**
 * Update the comment for an issue
 * @param projectId
 * @param issueId
 * @param commentId
 * @param message
 */
export const updateCommentForIssue = (projectId, issueId, commentId, message) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateCommentForIssue(projectId, issueId, commentId, message);
        await dataDispatch(fetchIssueComment(projectId, issueId, commentId)); // reload the comment
    };
};
/**
 * Delete a comment from an issue
 * @param projectId
 * @param issueId
 * @param commentId
 */
export const deleteCommentFromIssue = (projectId, issueId, commentId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().deleteCommentFromIssue(projectId, issueId, commentId);
        await dataDispatch(clearIssueComments(projectId, issueId)); // clear the current list of comments because it is no longer valid
    };
};
